import {Box, Center, Clickable, Right, Show, Spinner, Text} from 'platform/foundation';
import {NumberFormat} from 'platform/locale';

import {KeyboardEvent, MouseEvent} from 'react';

import {isNil, isNotNil} from 'ramda';

import {ApiError, Nullish, suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';

export interface DrillDownStockLinkProps extends TestIdProps {
  amount: number | Nullish;
  /**
   * Resolves to the ad server URL of the row. `null` keeps the value as plain text.
   */
  loadUrl: (() => Promise<string>) | null;
}

const ACTIVATION_KEYS = ['Enter', ' '];
const NEW_TAB_TARGET = '_blank';
const DETACHED_WINDOW_FEATURES = 'noopener,noreferrer';
// The blank tab shares this origin, so only web URLs may be loaded into it (never `javascript:`).
const AD_SERVER_URL_PROTOCOLS = ['http:', 'https:'];
const INVALID_AD_SERVER_URL_MESSAGE = 'The ad server URL is not an absolute http(s) URL.';

// The parsed URL is what gets loaded, so `https:host/path` cannot end up relative to this origin.
const parseAbsoluteWebUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    return AD_SERVER_URL_PROTOCOLS.includes(parsedUrl.protocol) ? parsedUrl : null;
  } catch {
    return null;
  }
};

/**
 * Stock (pcs) cell. When a URL loader is provided and there is stock, the value opens
 * the ad server listing matching the row in a new tab.
 */
export function DrillDownStockLink(props: DrillDownStockLinkProps) {
  const [isLoading, startLoading, stopLoading] = useBoolean();

  const openAdServer = () => {
    if (isNil(props.loadUrl) || isLoading) {
      return;
    }
    // Open the tab synchronously within the user gesture so popup blockers allow it,
    // then navigate it once the URL is loaded.
    const adServerWindow = window.open('', NEW_TAB_TARGET);
    // Detach the third-party ad server tab from this window.
    if (isNotNil(adServerWindow)) {
      adServerWindow.opener = null;
    }

    startLoading();
    // Starting from a resolved promise routes a loader that throws synchronously to the catch too.
    Promise.resolve()
      .then(props.loadUrl)
      .then((url) => {
        const adServerUrl = parseAbsoluteWebUrl(url);
        if (isNil(adServerUrl)) {
          throw new Error(INVALID_AD_SERVER_URL_MESSAGE);
        }
        if (isNil(adServerWindow)) {
          window.open(adServerUrl.href, NEW_TAB_TARGET, DETACHED_WINDOW_FEATURES);
          return;
        }
        adServerWindow.location.href = adServerUrl.href;
      })
      .catch((error: ApiError) => {
        adServerWindow?.close();
        handleApiError(error);
      })
      .finally(stopLoading);
  };

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    // Do not toggle the row expansion.
    event.stopPropagation();
    openAdServer();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // A held key repeats keydown, which would open a tab per repeat.
    if (!ACTIVATION_KEYS.includes(event.key) || event.repeat) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    openAdServer();
  };

  if (isNil(props.loadUrl) || (props.amount ?? 0) <= 0) {
    return (
      <Right>
        <Text size="xSmall">
          <NumberFormat number={props.amount} />
        </Text>
      </Right>
    );
  }

  return (
    <Right>
      <Clickable
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={onKeyDown}
        data-testid={suffixTestId('stockLink', props)}
      >
        <Box position="relative">
          <Text size="xSmall" color="link">
            <NumberFormat number={props.amount} />
          </Text>
          {/* The spinner overlays the space left of the number, so the number stays flush right
              like the plain values and does not move while the URL loads. */}
          <Show when={isLoading}>
            <Box position="absolute" top={0} right="100%" height="100%" paddingRight={1}>
              <Center height="100%">
                <Spinner size="xSmall" data-testid="drillDownStockLink" />
              </Center>
            </Box>
          </Show>
        </Box>
      </Clickable>
    </Right>
  );
}
